/**
 * Verification Service - API Communication Layer
 * Modular service for handling verification-related API calls
 */

import { AxiosProgressEvent } from 'axios';
import {
    DocumentUploadData,
    VerificationStatusResponse,
    TrustScoreHistoryResponse,
    UploadResponse,
    PermissionCheckResult,
    DocumentType,
    VerificationStatus
} from '../types/verification';
import api from './api';

class VerificationService {
    private readonly baseUrl = '/verification';
    private readonly documentUrl = '/verification/documents';
    private readonly REQUEST_TIMEOUT = 60000;
    private readonly MAX_RETRIES = 2;

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private isRetryableError(error: any): boolean {
        const status = error?.response?.status;

        return (
            status === 429 ||
            error?.code === 'ECONNABORTED' ||
            error?.code === 'ERR_NETWORK' ||
            !error?.response
        );
    }

    private getRetryDelayMs(error: any, attempt: number): number {
        const retryAfterHeader = error?.response?.headers?.['retry-after'];
        const retryAfterSeconds = Number(retryAfterHeader);

        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
            return retryAfterSeconds * 1000;
        }

        return 1000 * Math.pow(2, attempt);
    }

    private async withRetry<T>(operation: () => Promise<T>, context: string): Promise<T> {
        let lastError: any;

        for (let attempt = 0; attempt <= this.MAX_RETRIES; attempt++) {
            try {
                return await operation();
            } catch (error: any) {
                lastError = error;

                if (attempt === this.MAX_RETRIES || !this.isRetryableError(error)) {
                    break;
                }

                const delayMs = this.getRetryDelayMs(error, attempt);
                console.warn(`${context} failed (attempt ${attempt + 1}). Retrying in ${delayMs}ms...`, {
                    status: error?.response?.status,
                    code: error?.code,
                });
                await this.sleep(delayMs);
            }
        }

        throw lastError;
    }

    /**
     * Get user's verification status and documents
     */
    async getVerificationStatus(): Promise<VerificationStatusResponse> {
        try {
            const response = await this.withRetry(
                () => api.get(`${this.baseUrl}/status`, { timeout: this.REQUEST_TIMEOUT }),
                'getVerificationStatus'
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching verification status:', error);
            throw error;
        }
    }

    /**
     * Get trust score history
     */
    async getTrustScoreHistory(): Promise<TrustScoreHistoryResponse> {
        try {
            const response = await api.get(`${this.baseUrl}/trust-score/history`, { timeout: this.REQUEST_TIMEOUT });
            return response.data;
        } catch (error) {
            console.error('Error fetching trust score history:', error);
            throw error;
        }
    }

    /**
     * Check if user has permission for a specific action
     */
    async checkPermission(action: 'post_gig' | 'apply_gig', value: number): Promise<PermissionCheckResult> {
        try {
            const response = await api.post(`${this.baseUrl}/check-permission`, {
                action,
                value
            });
            return response.data.data;
        } catch (error) {
            console.error('Error checking permission:', error);
            throw error;
        }
    }

    /**
     * Upload a document for verification - supports both single and dual-sided documents
     */
    async uploadDocument(documentData: DocumentUploadData, onProgress?: (progress: number) => void): Promise<UploadResponse> {
        try {
            const formData = new FormData();
            formData.append('documentType', documentData.documentType);

            // Handle dual-sided documents (front and back files)
            if (documentData.frontFile && documentData.backFile) {
                formData.append('frontFile', documentData.frontFile);
                formData.append('backFile', documentData.backFile);
            }
            // Handle single-sided documents
            else if (documentData.file) {
                formData.append('singleFile', documentData.file);
            } else {
                throw new Error('Either provide a single file or both front and back files');
            }

            if (documentData.description) {
                formData.append('description', documentData.description);
            }

            const response = await api.post(`${this.documentUrl}/upload`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                    if (progressEvent.total && onProgress) {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress(progress);
                    }
                }
            });

            return response.data;
        } catch (error: any) {
            console.error('Error uploading document:', error);

            // Create a structured error with the backend message if available
            if (error.response?.data?.message) {
                const customError = new Error(error.response.data.message);
                customError.name = 'UploadError';
                // Preserve the original error properties
                (customError as any).response = error.response;
                (customError as any).status = error.response?.status;
                throw customError;
            }

            throw error;
        }
    }

    /**
     * Delete a document
     */
    async deleteDocument(documentId: string): Promise<{ success: boolean; message?: string }> {
        try {
            const response = await api.delete(`${this.documentUrl}/${documentId}`);
            return response.data;
        } catch (error: any) {
            console.error('Error deleting document:', error);

            // If document is already deleted (404), treat it as success
            if (error.response?.status === 404 ||
                error.response?.data?.message?.includes('already deleted')) {
                return {
                    success: true,
                    message: 'Document was already deleted'
                };
            }

            throw error;
        }
    }

    /**
     * Get user's documents
     */
    async getUserDocuments(): Promise<{ success: boolean; documents: any[] }> {
        try {
            const response = await this.withRetry(
                () => api.get(`${this.documentUrl}`, { timeout: this.REQUEST_TIMEOUT }),
                'getUserDocuments'
            );
            const data = response.data;

            // Handle the response structure from backend
            if (data.success && data.data) {
                return {
                    success: true,
                    documents: data.data.documents || []
                };
            }

            return {
                success: false,
                documents: []
            };
        } catch (error) {
            console.error('Error fetching user documents:', error);
            throw error;
        }
    }

    /**
     * Get verification requirements
     */
    async getVerificationRequirements(): Promise<{
        requiredDocuments: DocumentType[];
        optionalDocuments: DocumentType[];
        benefits: string[];
    }> {
        try {
            const response = await this.withRetry(
                () => api.get(`${this.baseUrl}/requirements`, { timeout: this.REQUEST_TIMEOUT }),
                'getVerificationRequirements'
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching verification requirements:', error);
            throw error;
        }
    }

    /**
     * Get user statistics
     */
    async getUserStats(): Promise<{
        trustScore: number;
        level: string;
        completedGigs: number;
        verificationStatus: VerificationStatus;
        joinDate: string;
        benefits: string[];
    }> {
        try {
            const response = await api.get(`${this.baseUrl}/statistics`);
            return response.data;
        } catch (error) {
            console.error('Error fetching user stats:', error);
            throw error;
        }
    }

    /**
     * Download document (for user's own documents)
     */
    async downloadDocument(documentId: string): Promise<Blob> {
        try {
            const response = await api.get(`${this.documentUrl}/${documentId}/file`, {
                responseType: 'blob'
            });
            return response.data;
        } catch (error) {
            console.error('Error downloading document:', error);
            throw error;
        }
    }

    /**
     * Get document data for viewing (with base64 data)
     */
    async getDocumentForViewing(documentId: string): Promise<{
        success: boolean;
        data: {
            id: string;
            fileName: string;
            mimeType: string;
            fileSize: number;
            base64Data: string;
            documentType: string;
            category: string;
            status: VerificationStatus;
            uploadedAt: string;
        };
    }> {
        try {
            const response = await api.get(`${this.documentUrl}/${documentId}/view`);
            return response.data;
        } catch (error) {
            console.error('Error fetching document for viewing:', error);
            throw error;
        }
    }

    /**
     * Resubmit a rejected document
     */
    async resubmitDocument(documentId: string, documentData: DocumentUploadData, onProgress?: (progress: number) => void): Promise<UploadResponse> {
        try {
            const formData = new FormData();
            if (documentData.file) {
                formData.append('document', documentData.file);
            }
            formData.append('documentType', documentData.documentType);
            if (documentData.documentSide) {
                formData.append('documentSide', documentData.documentSide);
            }
            if (documentData.description) {
                formData.append('description', documentData.description);
            }

            const response = await api.put(`${this.documentUrl}/${documentId}/resubmit`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                    if (progressEvent.total && onProgress) {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        onProgress(progress);
                    }
                }
            });

            return response.data;
        } catch (error) {
            console.error('Error resubmitting document:', error);
            throw error;
        }
    }
}

// Export singleton instance
export const verificationService = new VerificationService();
export default verificationService;